import { useCallback, useEffect, useRef, useState } from "react";
import {
  useBackendReconnectKey,
  useRefreshOnReconnect,
} from "@/hooks/use-backend-connectivity";
import { fetchWithAuth } from "@/lib/utils";
import type {
  ChildSessionSummary,
  ChildSessionListResponse,
  ThreadAnchorRef,
  ThreadAnchorQueryResponse,
  ThreadCreateRequest,
  ThreadCreateResponse,
} from "@/lib/session";
import i18n from "@/lib/i18n";

const API_BASE = "/api/sessions";

const jsonHeaders: Record<string, string> = {
  "Content-Type": "application/json",
};

/**
 * Lazy-load direct child sessions of a parent session.
 *
 * Fetches `GET /api/sessions/{parentId}/children?types=thread&limit=50`.
 * Returns an empty list when `parentId` is null or `enabled` is false.
 */
export function useChildSessions(
  parentId: string | null,
  getToken: () => Promise<string>,
  opts?: {
    types?: string;
    enabled?: boolean;
    includeNavigation?: boolean;
    refreshNonce?: number;
    initialChildren?: ChildSessionListResponse;
    pending?: boolean;
  },
) {
  const types = opts?.types ?? "thread";
  const enabled = opts?.enabled ?? true;
  const includeNavigation = opts?.includeNavigation ?? false;
  const refreshNonce = opts?.refreshNonce ?? 0;
  const initialChildren = opts?.initialChildren;
  const pending = opts?.pending ?? false;
  const [children, setChildren] = useState<ChildSessionSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const requestGenerationRef = useRef(0);
  const initialRefreshNonce = useRef(refreshNonce);

  const mergeChildren = useCallback((
    previous: ChildSessionSummary[],
    incoming: ChildSessionSummary[],
    replace: boolean,
  ) => {
    const byId = new Map<string, ChildSessionSummary>();
    if (!replace) previous.forEach((child) => byId.set(child.id, child));
    incoming.forEach((child) => byId.set(child.id, child));
    return [...byId.values()].sort((a, b) => (
      b.last_active_at.localeCompare(a.last_active_at) || b.id.localeCompare(a.id)
    ));
  }, []);

  const fetchChildren = useCallback(async () => {
    const generation = ++requestGenerationRef.current;
    if (!parentId || !enabled) {
      setChildren([]);
      setNextCursor(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("types", types);
      params.set("limit", "50");
      if (includeNavigation) params.set("include_navigation", "true");
      const url = `${API_BASE}/${parentId}/children?${params}`;
      const res = await fetchWithAuth(url, { headers: {} }, getToken);
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data: ChildSessionListResponse = await res.json();
      if (requestGenerationRef.current !== generation) return;
      setChildren((previous) => mergeChildren(previous, data.items ?? [], true));
      setNextCursor(data.next_cursor ?? null);
    } catch (err: any) {
      if (requestGenerationRef.current !== generation) return;
      setError(err?.message ?? i18n.t("threads.errors.loadChildren"));
    } finally {
      if (requestGenerationRef.current === generation) setLoading(false);
    }
  }, [parentId, types, enabled, getToken, includeNavigation, mergeChildren, refreshNonce]);

  const loadMore = useCallback(async () => {
    if (!parentId || !enabled || !nextCursor || loadingMore) return;
    const generation = requestGenerationRef.current;
    setLoadingMore(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        types,
        limit: "50",
        before: nextCursor,
      });
      if (includeNavigation) params.set("include_navigation", "true");
      const res = await fetchWithAuth(
        `${API_BASE}/${parentId}/children?${params}`,
        { headers: {} },
        getToken,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data: ChildSessionListResponse = await res.json();
      if (requestGenerationRef.current !== generation) return;
      setChildren((previous) => mergeChildren(previous, data.items ?? [], false));
      setNextCursor(data.next_cursor ?? null);
    } catch (err: any) {
      if (requestGenerationRef.current === generation) {
        setError(err?.message ?? i18n.t("threads.errors.loadChildren"));
      }
    } finally {
      if (requestGenerationRef.current === generation) setLoadingMore(false);
    }
  }, [enabled, getToken, includeNavigation, loadingMore, mergeChildren, nextCursor, parentId, types]);

  useRefreshOnReconnect(fetchChildren, Boolean(parentId && enabled));

  useEffect(() => {
    requestGenerationRef.current += 1;
    setChildren([]);
    setNextCursor(null);
    setError(null);
  }, [parentId, enabled, types, includeNavigation]);

  useEffect(() => {
    if (initialChildren && refreshNonce === initialRefreshNonce.current) {
      requestGenerationRef.current += 1;
      setChildren(initialChildren.items);
      setNextCursor(initialChildren.next_cursor);
      setLoading(false);
      setError(null);
      return;
    }
    if (pending) return;
    fetchChildren();
  }, [fetchChildren, initialChildren, pending, refreshNonce]);

  return {
    children,
    loading,
    loadingMore,
    error,
    hasMore: nextCursor !== null,
    fetchedCount: children.length,
    loadMore,
    refetch: fetchChildren,
  };
}

/**
 * Batch query thread anchors for a set of visible message IDs.
 *
 * Debounced by 500ms and deduplicated by message ID set. Pass a changing
 * `refreshNonce` to re-query without changing the visible message list.
 * Returns `{ anchors, loading }`.
 */
export function useChildSessionAnchors(
  parentId: string | null,
  visibleMessageIds: string[],
  getToken: () => Promise<string>,
  opts?: { types?: string; enabled?: boolean; refreshNonce?: number; initialAnchors?: ThreadAnchorRef[]; pending?: boolean },
) {
  const enabled = opts?.enabled ?? true;
  const refreshNonce = opts?.refreshNonce ?? 0;
  const initialAnchors = opts?.initialAnchors;
  const pending = opts?.pending ?? false;
  const [anchors, setAnchors] = useState<ThreadAnchorRef[]>([]);
  const [loading, setLoading] = useState(false);

  // Deduplicate and sort the message IDs so the effect dependency is stable.
  const msgKey = [...new Set(visibleMessageIds)].sort().join(",");
  const initialRefreshNonce = useRef(refreshNonce);

  // Refs to read latest in the debounced callback
  const parentIdRef = useRef(parentId);
  const getTokenRef = useRef(getToken);
  const anchorRequestRef = useRef(0);
  const backendReconnectKey = useBackendReconnectKey();
  useEffect(() => { parentIdRef.current = parentId; }, [parentId]);
  useEffect(() => { getTokenRef.current = getToken; }, [getToken]);

  useEffect(() => {
    const requestId = anchorRequestRef.current + 1;
    anchorRequestRef.current = requestId;
    let cancelled = false;
    const isCurrentRequest = () => !cancelled && anchorRequestRef.current === requestId;

    if (initialAnchors && refreshNonce === initialRefreshNonce.current) {
      setAnchors(initialAnchors);
      setLoading(false);
      return () => { cancelled = true; };
    }
    if (pending) return () => { cancelled = true; };

    if (!parentId || !enabled || !msgKey) {
      setAnchors([]);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    const ids = msgKey.split(",").filter(Boolean);
    if (ids.length === 0) {
      setAnchors([]);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const timer = setTimeout(async () => {
      if (!isCurrentRequest()) return;
      setLoading(true);
      try {
        const url = `${API_BASE}/${parentIdRef.current}/children/anchors/query`;
        const res = await fetchWithAuth(
          url,
          {
            method: "POST",
            headers: jsonHeaders,
            body: JSON.stringify({
              message_ids: ids,
              include_archived: false,
            }),
          },
          getTokenRef.current,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data: ThreadAnchorQueryResponse = await res.json();
        if (!isCurrentRequest()) return;
        setAnchors(data.items ?? []);
      } catch {
        // Silently fail — anchor indicators are non-critical
        if (isCurrentRequest()) setAnchors([]);
      } finally {
        if (isCurrentRequest()) setLoading(false);
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [backendReconnectKey, parentId, msgKey, enabled, refreshNonce, initialAnchors, pending]);

  return { anchors, loading };
}

/**
 * Create a thread child session from a message or paragraph anchor.
 *
 * Returns `{ createThread, creating, error }`.
 * `createThread` calls `POST /api/sessions/{parentId}/threads` and
 * returns the `ThreadCreateResponse` on success, or `null` on failure.
 */
export function useCreateThread(
  parentId: string,
  getToken: () => Promise<string>,
) {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createThread = useCallback(
    async (req: ThreadCreateRequest): Promise<ThreadCreateResponse | null> => {
      setCreating(true);
      setError(null);
      try {
        const url = `${API_BASE}/${parentId}/threads`;
        const res = await fetchWithAuth(
          url,
          {
            method: "POST",
            headers: jsonHeaders,
            body: JSON.stringify(req),
          },
          getToken,
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        const data: ThreadCreateResponse = await res.json();
        return data;
      } catch (err: any) {
        setError(err?.message ?? i18n.t("threads.errors.create"));
        return null;
      } finally {
        setCreating(false);
      }
    },
    [parentId, getToken],
  );

  return { createThread, creating, error };
}
